/**
 * Variacao contra o periodo anterior: glifo + texto, nunca so cor.
 *
 * Copia local do contrato de features/admin-central/components/Delta.tsx
 * (modulos nao importam um do outro; pedido de promocao ao kit no relatorio).
 *
 * Decisoes nao obvias:
 *  1. O glifo e decorativo e o texto diz "alta de 12,3%": quem nao distingue
 *     verde de vermelho, e o leitor de tela, recebem a mesma informacao.
 *  2. Medida em bps (taxa) nao mostra variacao relativa ("+15% na taxa" e
 *     ambiguo): mostra "de 18,0% para 21,1%".
 *  3. `compacto` e a forma de coluna de tabela: glifo + relativo, com o texto
 *     completo no `title`.
 *  4. `monocromatico` e para o cartao de destaque (fundo em gradiente): a cor
 *     de status nao tem contraste garantido ali, entao o delta herda a cor do
 *     texto e a direcao fica no glifo + palavra.
 */

import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../../lib/format';
import { variar } from '../comparar';

export type TipoDeMedida = 'dinheiro' | 'contagem' | 'bps';
export type SentidoDoDelta = 'maior_melhor' | 'menor_melhor' | 'neutro';

const TITULO = 'Comparado com o período anterior de mesmo tamanho';

function formatarAbsoluto(valor: number, tipo: TipoDeMedida): string {
  const sinal = valor > 0 ? '+' : valor < 0 ? '−' : '';
  const abs = Math.abs(valor);
  return `${sinal}${tipo === 'dinheiro' ? formatBRLFromCents(abs) : formatCount(abs)}`;
}

function corDo(alta: boolean, sentido: SentidoDoDelta, monocromatico: boolean): string {
  if (monocromatico) {
    return '';
  }
  if (sentido === 'neutro') {
    return 'text-ink-muted';
  }
  const bom = sentido === 'maior_melhor' ? alta : !alta;
  return bom ? 'text-status-good' : 'text-status-critical';
}

export function Delta({
  atual,
  anterior,
  tipo,
  sentido = 'maior_melhor',
  compacto = false,
  monocromatico = false,
  className = '',
}: {
  readonly atual: number;
  /** `null` quando nao ha periodo anterior honesto. */
  readonly anterior: number | null;
  readonly tipo: TipoDeMedida;
  readonly sentido?: SentidoDoDelta;
  readonly compacto?: boolean;
  readonly monocromatico?: boolean;
  readonly className?: string;
}): JSX.Element {
  const variacao = variar(atual, anterior);
  const base = `inline-flex flex-wrap items-center gap-1 text-xs tabular-nums ${className}`;
  const apagado = monocromatico ? 'opacity-80' : 'text-ink-subtle';

  if (variacao.direcao === 'sem_base') {
    return (
      <span className={`${base} ${apagado}`} title={`${TITULO}: não havia período anterior para comparar`}>
        {compacto ? '—' : 'sem base anterior'}
        {compacto ? <span className="sr-only">sem base anterior</span> : null}
      </span>
    );
  }

  // Taxa que muda menos que a casa decimal exibida seria "queda de 14,0% para 14,0%".
  const igualNaTela = tipo === 'bps' && anterior !== null && formatPercentFromBps(atual) === formatPercentFromBps(anterior);

  if (variacao.direcao === 'igual' || igualNaTela) {
    return (
      <span className={`${base} ${apagado}`} title={TITULO}>
        <span aria-hidden="true">=</span> {compacto ? 'igual' : 'igual ao anterior'}
      </span>
    );
  }

  const alta = variacao.direcao === 'alta';
  const cor = corDo(alta, sentido, monocromatico);
  const glifo = alta ? '▲' : '▼';
  const palavra = alta ? 'alta' : 'queda';

  if (tipo === 'bps') {
    const texto = `de ${formatPercentFromBps(anterior ?? 0)} para ${formatPercentFromBps(atual)}`;
    return (
      <span className={`${base} ${cor}`} title={`${TITULO}: ${texto}`}>
        <span aria-hidden="true">{glifo}</span>
        <span className="sr-only">{palavra}:</span>
        <span>{compacto ? `antes ${formatPercentFromBps(anterior ?? 0)}` : texto}</span>
      </span>
    );
  }

  const absoluto = formatarAbsoluto(variacao.absoluta, tipo);
  const relativo = variacao.relativaBps === null ? null : formatPercentFromBps(Math.abs(variacao.relativaBps));
  const textoCompleto = relativo === null ? `${absoluto} (antes: 0)` : `${relativo} (${absoluto})`;

  if (compacto) {
    return (
      <span className={`${base} ${cor}`} title={`${TITULO}: ${palavra} de ${textoCompleto}`}>
        <span aria-hidden="true">{glifo}</span>
        <span className="sr-only">{palavra} de</span>
        <span>{relativo ?? absoluto}</span>
      </span>
    );
  }

  return (
    <span className={`${base} ${cor}`} title={TITULO}>
      <span aria-hidden="true">{glifo}</span>
      <span className="sr-only">{palavra} de</span>
      {relativo === null ? (
        <span>
          {absoluto} <span className={apagado}>(antes: 0)</span>
        </span>
      ) : (
        <span>
          {relativo} <span className={apagado}>({absoluto} vs. anterior)</span>
        </span>
      )}
    </span>
  );
}
